// src/components/results/WritingTab.jsx
//
// Schreiben: the score, the student's errors grouped by the same taxonomy as the rest
// of the paper (from the AI correction's `tag`), and the existing corrected-text view
// (`writingSlot`, unchanged). Without tags the errors-by-type block simply stays away.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { familyEntry, fmt, pick, practiceLinks } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };
const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

export default function WritingTab({
  score, writingSlot, writingCorrections = [], items, idx, exam, isCorrecting, isFree, onPractice, onLocked, onTab,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  // Tagged errors → language families, most frequent first
  const types = useMemo(() => {
    const groups = new Map();
    (writingCorrections || []).forEach((c) => {
      const point = c?.tag ? idx.point[c.tag] : null;
      if (!point) return;
      const fam = familyEntry(idx, 'language', point.family);
      const cur = groups.get(point.family) || { id: point.family, label: pick(fam?.label, lang), tip: pick(point.tip, lang) || pick(fam?.tip, lang), points: new Map(), count: 0 };
      cur.count += 1; cur.points.set(c.tag, (cur.points.get(c.tag) || 0) + 1);
      groups.set(point.family, cur);
    });
    return [...groups.values()].sort((a, b) => b.count - a.count).map((g) => ({
      ...g,
      topPoint: [...g.points.entries()].sort((a, b) => b[1] - a[1])[0][0],
      // the same family went wrong in the gap texts too
      inSprach: items.filter((it) => it.section === 'sprachbausteine' && !it.correct && it.languageFamily === g.id).map((it) => it.number),
    }));
  }, [writingCorrections, idx, items, lang]);

  const top = types[0];
  const note = isCorrecting
    ? t('examResults.story.waitingForWriting')
    : top
      ? (top.inSprach.length ? t('examResults.story.writing.noteSame', { what: top.label }) : t('examResults.story.writing.noteTop', { what: top.label, count: top.count }))
      : t('examResults.story.writing.note');
  const ruleLinks = top ? practiceLinks(idx.point[top.topPoint], exam.level, exam.examProvider, t) : [];

  return (
    <div className="flex flex-col gap-9">
      <section className="rs-up rs-paper flex flex-col gap-1.5 px-6 sm:px-7 pt-6 pb-7" style={{ borderTop: '6px solid #1C1917', backgroundPosition: '0 24px' }}>
        <h2 className="m-0 text-[30px] sm:text-[34px] font-bold" style={DISPLAY}>{t('examResults.story.writing.name')}</h2>
        {score && !isCorrecting && <span className="text-[54px] leading-none" style={HAND}>{fmt(score.score)} / {fmt(score.max)}</span>}
        <p className="m-0 mt-1 text-[26px] sm:text-[28px] leading-[1.2] text-red-600" style={HAND}>
          <span className="redpen-note" style={{ animationDelay: '.6s' }}>{note}</span>
        </p>
      </section>

      {types.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="m-0 text-2xl font-bold" style={DISPLAY}>{t('examResults.story.writing.typesTitle')}</h2>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr))]">
            {types.map((g) => (
              <div key={g.id} className="bg-white rounded-md px-4 py-3.5 grid grid-cols-[50px_minmax(0,1fr)] gap-3 items-center shadow-[0_14px_30px_-26px_rgba(28,25,23,.5)]">
                <span className="text-[34px] leading-none text-red-600 text-center" style={HAND}>{g.count}×</span>
                <span className="flex flex-col gap-0.5 items-start">
                  <span className="font-bold">{g.label}</span>
                  {g.tip && <span className="text-sm text-stone-700 leading-relaxed">{g.tip}</span>}
                  {g.inSprach.length > 0 && (
                    <button type="button" onClick={() => onTab('sprachbausteine')} className="rs-focus mt-1 min-h-[32px] text-[13px] font-bold text-red-700 text-left">
                      {t('examResults.story.writing.samePattern', { gaps: g.inSprach.join(', ') })} →
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {writingSlot && (
        <section className="flex flex-col gap-3">
          <h2 className="m-0 text-2xl font-bold" style={DISPLAY}>{t('examResults.story.writing.correctedTitle')}</h2>
          <div className="bg-white rounded-[10px] overflow-hidden shadow-[0_18px_40px_-30px_rgba(28,25,23,.45)]">{writingSlot}</div>
        </section>
      )}

      {(isFree || ruleLinks.length > 0) && (
        <section className="rs-up grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr))]">
          {isFree && (
            <div className="bg-stone-900 text-white rounded-[10px] p-6 flex flex-col gap-2.5">
              <span className="text-[26px] leading-tight text-red-300" style={HAND}>{t('examResults.story.writing.teacherKicker')}</span>
              <h3 className="m-0 text-xl leading-snug font-bold" style={DISPLAY}>{t('examResults.story.writing.teacherTitle')}</h3>
              <p className="m-0 text-sm leading-relaxed text-stone-300">{t('examResults.story.writing.teacherText')}</p>
              <button type="button" onClick={onLocked} className="rs-focus self-start mt-1 min-h-[48px] px-4 rounded-[10px] bg-white text-stone-900 font-bold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform">
                <Lock className="w-4 h-4" aria-hidden="true" />{t('examResults.story.writing.teacherCta')}
              </button>
            </div>
          )}
          {ruleLinks.length > 0 && (
            <div className="bg-white border-2 border-stone-900 rounded-[10px] p-6 flex flex-col gap-2.5">
              <span className="text-[26px] leading-tight text-red-600" style={HAND}>{t('examResults.story.writing.ruleKicker')}</span>
              <h3 className="m-0 text-xl leading-snug font-bold" style={DISPLAY}>{pick(idx.point[top.topPoint]?.label, lang)}</h3>
              <p className="m-0 text-sm leading-relaxed text-stone-700">{pick(idx.point[top.topPoint]?.tip, lang)}</p>
              <div className="flex flex-wrap gap-2.5 mt-1">
                {ruleLinks.map((l) => {
                  const locked = l.premium && isFree;
                  return (
                    <button key={l.label} type="button" onClick={() => (locked ? onLocked() : onPractice(l))}
                      className={`rs-focus min-h-[48px] px-4 rounded-[10px] font-bold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform ${l.premium ? 'bg-stone-900 text-white' : 'border-2 border-stone-900 text-stone-900'}`}>
                      {locked && <Lock className="w-4 h-4" aria-hidden="true" />}
                      {l.label} · {l.premium ? t('examResults.story.premium') : t('examResults.story.free')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
